import React, { useState, useEffect } from 'react';
import { TextField, IconButton, Button, Autocomplete, Box, Typography, Stack, Divider, Paper } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import API_BASE_URL from '../config';
import { styled } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import SchoolIcon from '@mui/icons-material/School';

// Styled components
const EducationCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    borderRadius: theme.spacing(2),
    background: `linear-gradient(to bottom right, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.paper, 0.95)})`,
    backdropFilter: 'blur(10px)',
    boxShadow: `0 8px 32px 0 ${alpha(theme.palette.common.black, 0.08)}`,
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    },
    '&:hover': {
        boxShadow: `0 12px 40px 0 ${alpha(theme.palette.common.black, 0.12)}`,
    },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: theme.spacing(1.5),
        transition: 'all 0.3s ease',
        '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.02),
        },
        '&.Mui-focused': {
            backgroundColor: alpha(theme.palette.primary.main, 0.03),
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
                borderWidth: '2px',
            },
        },
    },
    '& .MuiInputLabel-root': {
        '&.Mui-focused': {
            color: theme.palette.primary.main,
        },
    },
}));

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: theme.spacing(1.5),
        transition: 'all 0.3s ease',
        '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.02),
        },
        '&.Mui-focused': {
            backgroundColor: alpha(theme.palette.primary.main, 0.03),
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
                borderWidth: '2px',
            },
        },
    },
    '& .MuiInputLabel-root': {
        '&.Mui-focused': {
            color: theme.palette.primary.main,
        },
    },
}));

const InfoTypeIcon = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    borderRadius: '50%',
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
    marginRight: theme.spacing(2),
  }));
const ActionButton = styled(Button)(({ theme }) => ({
    borderRadius: theme.spacing(3),
    padding: theme.spacing(1, 3),
    textTransform: 'none',
    fontWeight: 600,
    color: 'white',
    transition: 'all 0.3s ease',
    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 5px 15px ${alpha(theme.palette.primary.main, 0.3)}`,
    },
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
    color: theme.palette.error.main,
    transition: 'all 0.2s ease',
    '&:hover': {
        backgroundColor: alpha(theme.palette.error.main, 0.1),
        transform: 'scale(1.1)',
    },
}));

const EducationDetailForm = ({ formData, editMode, setAllEducationDetails }) => {
    const [educationDetails, setEducationDetails] = useState([]);
    const [suggestions, setSuggestions] = useState([]);

    // Education levels that will act as suggestions
    const educationLevelSuggestions = [
        'High School', 'Undergraduate', 'Postgraduate', 'Doctorate', 'Diploma', 'Certification'
    ];

    const getEducationHeading = (level) => {
        if (!level) return null;
        const l = level.toLowerCase();
        if (l.includes('high school') || l.includes('school')) return 'School Education';
        if (l.includes('undergraduate') || l.includes('college') || l.includes('diploma') || l.includes('certification')) return 'College Education';
        if (l.includes('postgraduate') || l.includes('doctorate')) return 'Higher Education';
        return null;
    };

    const formDataId = formData?.id;

    // Fetch education details based on user_id when component mounts
    useEffect(() => {
        if (!formDataId) return;
        const fetchEducationDetails = async () => {
            try {
                    const response = await axios.get(`${API_BASE_URL}/education/${formDataId}`, {
                        withCredentials: true
                    });
                    console.log('response-education', response.data);
                    if (response.data && response.data.educationDetails) {
                        if (response.data.educationDetails.length > 0) {
                            setEducationDetails(response.data.educationDetails);
                        } else {
                            setEducationDetails([{
                               // id: 1,
                                user_id: formDataId || '',
                                level: '',
                                institute: '',
                                start_year: '',
                                end_year: '',
                            }]);
                        }
                    } else {
                        setEducationDetails([{
                            id: 1,
                            user_id: formDataId || '',
                            level: '',
                            institute: '',
                            start_year: '',
                            end_year: '',
                        }]);
                    }
            } catch (error) {
                    console.error('Error fetching education details:', error);
                    if (error.response?.status === 401) {
                        console.log('User not authenticated, redirecting to login...');
                    } else if (error.response?.status === 404) {
                        console.log('Education details not found');
                        setEducationDetails([{ id: 1, user_id: formDataId || '', level: '', institute: '', start_year: '', end_year: '' }]);
                    } else {
                        console.error('Error fetching education details:', error);
                        setEducationDetails([{ id: 1, user_id: formDataId || '', level: '', institute: '', start_year: '', end_year: '' }]);
                    }
            }
        };
        fetchEducationDetails();
    }, [formDataId]);

    const handleAddEducation = () => {
        setEducationDetails([
            ...educationDetails,
            {
               // id: educationDetails.length + 1,
                user_id: formData.id || '',
                level: '',
                institute: '',
                start_year: '',
                end_year: '',
            }
        ]);
    };

    const handleRemoveEducation = async (index, educationId) => {
        if (!educationId) {
            // If educationId is not available, remove it from state only
            const newEducationDetails = [...educationDetails];
            newEducationDetails.splice(index, 1);
            setEducationDetails(newEducationDetails);
            return;
        }

        try {
            await axios.delete(`${API_BASE_URL}/education/${educationId}`);
            const newEducationDetails = educationDetails.filter((edu) => edu.id !== educationId);
            setEducationDetails(newEducationDetails);
        } catch (error) {
            console.error('Error deleting education detail:', error);
        }
    };

    const handleFieldChange = (index, field, value) => {
        const newEducationDetails = [...educationDetails];
        newEducationDetails[index][field] = value;
        setEducationDetails(newEducationDetails);
        setAllEducationDetails(newEducationDetails);
    };

    const handleSearch = async (index, field, value) => {
        if (value) {
            try {
                const response = await axios.get(`${API_BASE_URL}/search`, {
                    params: {
                        [field]: value
                    }
                });

                if (response.data.length > 0) {
                    setSuggestions(response.data);
                } else {
                    setSuggestions([]);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                setSuggestions([]);
            }
        } else {
            setSuggestions([]);
        }
    };

    const handleSuggestionSelect = (index, option) => {
        const newEducationDetails = [...educationDetails];
        newEducationDetails[index].institute = option.institute;
        setEducationDetails(newEducationDetails);
        setSuggestions([]); // Clear suggestions after selection
    };

    return (
        <Box>
            <Stack spacing={3}>
                {educationDetails.map((detail, index) => (
                    <EducationCard key={index}>
                        <Stack spacing={2}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                            <InfoTypeIcon sx={{
                        ...(editMode ? {
                          color: (theme) => theme.palette.primary.main,
                          opacity: 1
                        } : {
                          color: 'text.secondary',
                          opacity: 0.6
                        })
                      }}>
                                <SchoolIcon
                                />
                                </InfoTypeIcon>
                                <Typography
                                    variant="h6"
                                    fontWeight="bold"
                                    sx={{
                                        ...(editMode ? {
                                            background: (theme) => `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                            backgroundClip: 'text',
                                            WebkitBackgroundClip: 'text',
                                            color: 'transparent',
                                        } : {
                                            color: 'text.secondary',
                                            opacity: 0.7
                                        })
                                    }}
                                >
                                    {detail.level || (index === 0 ? 'Primary Education' : `Education ${index + 1}`)}
                                </Typography>
                            </Stack>

                            <Divider />

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <StyledAutocomplete
                                    freeSolo
                                    options={educationLevelSuggestions}
                                    value={detail.level}
                                    onInputChange={(event, newInputValue) => {
                                        handleFieldChange(index, 'level', newInputValue);
                                    }}
                                    renderInput={(params) => (
                                        <StyledTextField
                                            {...params}
                                            label="Education Level"
                                            fullWidth
                                            disabled={!editMode}
                                        />
                                    )}
                                    sx={{ flex: 1, width: '100%' }}
                                />

                                <StyledAutocomplete
                                    freeSolo
                                    options={suggestions.map((option) => option?.institute || '')}
                                    inputValue={detail.institute}
                                    onInputChange={(event, newInputValue) => {
                                        handleSearch(index, 'institute', newInputValue);
                                        handleFieldChange(index, 'institute', newInputValue);
                                    }}
                                    onChange={(event, newValue) => {
                                        const selectedOption = suggestions.find(option => option.institute === newValue);
                                        if (selectedOption) {
                                            handleSuggestionSelect(index, selectedOption);
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <StyledTextField
                                            {...params}
                                            label="School/College Name"
                                            fullWidth
                                            disabled={!editMode}
                                        />
                                    )}
                                    sx={{ flex: 1, width: '100%' }}
                                />

                                <StyledTextField
                                    label="Start Year"
                                    type="number"
                                    inputProps={{ min: 1900, max: new Date().getFullYear(), maxLength: 4, step: 1 }}
                                    value={detail.start_year}
                                    // onChange={(e) => {
                                    //     const value = e.target.value;
                                    //     if (value.length <= 4) {
                                    //         handleFieldChange(index, 'start_year', value);
                                    //     }
                                    // }}

onChange={(e) => {
    const value = e.target.value;
    if (value.length <= 4) {
        handleFieldChange(index, 'start_year', value ? Number(value) : null);
    }
}}

                                    fullWidth
                                    disabled={!editMode}
                                    sx={{ flex: 1, width: '100%' }}
                                />

                                <StyledTextField
                                    select
                                    label="End Year"
                                    value={detail.end_year}
                                    //onChange={(e) => handleFieldChange(index, 'end_year', e.target.value)}
                                    onChange={(e) => handleFieldChange(index, 'end_year', e.target.value ? Number(e.target.value) : null)}
                                    fullWidth
                                    disabled={!editMode}
                                    SelectProps={{ native: true }}
                                    sx={{ flex: 1, width: '100%' }}
                                >
                                    <option value="" aria-label="None" />
                                    {[...Array(30)].map((_, i) => {
                                        const year = new Date().getFullYear() - i;
                                        return <option key={year} value={year}>{year}</option>;
                                    })}
                                </StyledTextField>

                                {index > 0 && (
                                    <DeleteButton
                                        onClick={() => handleRemoveEducation(index, detail.id)}
                                        disabled={!editMode}
                                    >
                                        <DeleteIcon />
                                    </DeleteButton>
                                )}
                            </Stack>
                        </Stack>
                    </EducationCard>
                ))}

                <Box sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    mt: 2
                }}>
                  {editMode && (
                    <ActionButton
                        onClick={handleAddEducation}
                        disabled={!editMode}
                        startIcon={<AddIcon />}
                    >
                        Add Education Detail
                    </ActionButton>
                    )}
                </Box>
            </Stack>
        </Box>
    );
};

export default EducationDetailForm;

